import React from 'react';

import { ErrorBoundary } from '@nxte-nl/error-boundary';

import {
  Props as BlogArticleBlockItemProps,
} from
  '../../components/BlogArticleBlockItem/BlogArticleBlockItem';
import { LandingPageBody } from '../../components/LandingPageBody/LandingPageBody';
import { LandingPageBodyV2 } from '../../components/LandingPageBodyV2/LandingPageBodyV2';
import NotFoundPage from '../../components/NotFoundPage/NotFoundPage';
import { entityHooks } from '../../hooks/entityHooks/entityHooks';
import { featureStateHooks } from '../../hooks/featureStateHooks/featureStateHooks';
import { layoutHooks } from '../../hooks/layoutHooks/layoutHooks';
import { pageStateHooks } from '../../hooks/pageStateHooks/pageStateHooks';
import { BlogBlock, CB_LandingPage, CB_LandingPageV2 } from '../../types/apiTypes';
import { StructuredDataContainer } from '../StructuredDataContainer/StructuredDataContainer';

export const hooks = {
  useMapFullwidthBlocks(data: BlogBlock[] | null):
   BlogArticleBlockItemProps[] | null {
    return React.useMemo(() => {
      if (!data) return null;
      return data.map((block) => {
        return {
          coloredBlocks: block.coloredBlock ? block.coloredBlock.map(el => ({
            title: el.title,
            referencedPath: el.referencedPath,
            description: el.description,
            image: el.image,
            backgroundColor: el.backgroundColor,
            referencedPath_v1: el.referencedPath_v1,
          })) : null,
          firstDescription: block.firstDescription,
          secondDescription: block.secondDescription,
          title: block.title,
          image: block.image,
          withHandBlockTitle: block.withHandBlock ? block.withHandBlock.title : null,
          withHandBlockLink: block.withHandBlock
            ? block.withHandBlock.link : null,
          withHandBlockDescription: block.withHandBlock ? block.withHandBlock.description : null,
          withHandBlockCardImage: block.withHandBlock ? block.withHandBlock.cardImage : null,
          withHandBlockIsShowHand: block.withHandBlock ? block.withHandBlock.showHand : null,
          withHandBlockIsShowShadow: block.withHandBlock ? block.withHandBlock.showShadow : null,
          withHandBlockBackgroundImage: block.withHandBlock
            ? block.withHandBlock?.backgroundImage : null,
        };
      });
    }, [data]);
  },
};

export const typeGuards = {
  isV2(data: CB_LandingPage | CB_LandingPageV2 | null) {
    return data?.__typename === 'CB_LandingPageV2';
  },
  isV1(data: CB_LandingPage | CB_LandingPageV2 | null) {
    return data?.__typename === 'CB_LandingPage';
  },
};

const LandingPageContainer = () => {
  const data = entityHooks.useLandingPage();
  const v1Data = data as CB_LandingPage;
  const v2Data = data as CB_LandingPageV2;
  const fullwidthBlocks = hooks.useMapFullwidthBlocks(
    (data as CB_LandingPage)?.fullwidthBlocks ?? null,
  );
  const { entityLoaded } = pageStateHooks.useEntityLoaded();
  if (!data && entityLoaded) {
    return <NotFoundPage />;
  }
  const { isLandingPageV2On } = featureStateHooks.useFeatureToggles();

  const { isDesktop } = layoutHooks.useDesktop();

  return (
    <>
      <StructuredDataContainer breadCrumbItems={data?.breadcrumbs} />
      <ErrorBoundary>
        {(typeGuards.isV2(data) && isLandingPageV2On)
          && <LandingPageBodyV2
            isDesktop={isDesktop}
            bigBottomBlock={v2Data.bigBottomBlock}
            blocksRows={v2Data.blocksRows}
            fullWidthBlocks={v2Data.fullWidthBlocks}
            heroButton={v2Data.heroButton}
            heroImage={v2Data.heroImage}
            heroTitle={v2Data.heroTitle}
            heroDescription={v2Data.heroDescription}
            heroUsp={v2Data.heroUsp}
            topBlockDescription={v2Data.topBlockDescription}
            topBlockTitle={v2Data.topBlockTitle}
            topBlockUsps={v2Data.topBlockUsps}
          />
        }
        {(typeGuards.isV1(data)) && (
          <LandingPageBody
            heroImage={v1Data.heroImage}
            title={v1Data.title}
            description={v1Data.description}
            fullwidthBlocks={fullwidthBlocks}
            breadcrumbs={v1Data.breadcrumbs}
            bottomBlocks={v1Data.bottomBlocks}
          />
        )}
      </ErrorBoundary>
    </>
  );
};

LandingPageContainer.displayName = 'LandingPageContainer';

export default LandingPageContainer;
